import React from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from '~/common/components/PageTitle';

import { useAuthContext } from '~/common/contexts/auth';
import { useEnterprises } from '~/common/contexts/enterprises';
import { EditVariant } from '../types';

import { type Team } from '~/common/api-client';
import billingClient, {
  type EnterpriseMembers
} from '~/common/utils/billing-client/client-v2';

import useV2EnterpriseMembers from '~/common/hooks/useV2EnterpriseMembers';
import globalStyles from '../app.css';
import TeamSettings from '../containers/TeamSettings';
import {
  splitUserIdsAndEmails,
  updatePendingUserTeamAccess
} from '../utils/teams-utils';

export const addUsersByIdOrEmail = async ({
  token,
  enterpriseId,
  teamId,
  enterpriseMembers,
  userIdsOrEmails
}: {
  token: string;
  enterpriseId: string;
  teamId: string;
  enterpriseMembers: EnterpriseMembers.PendingOrAcceptedMember[];
  userIdsOrEmails: string[];
}) => {
  if (!userIdsOrEmails.length) {
    return;
  }

  const { keycloakIds, invitedEmails } = splitUserIdsAndEmails(userIdsOrEmails);

  if (keycloakIds.length) {
    await billingClient.teamMemberships.create({
      token,
      enterpriseId,
      id: teamId,
      keycloakIds
    });
  }

  if (invitedEmails.length) {
    await updatePendingUserTeamAccess({
      token,
      enterpriseId,
      invitedEmails,
      teamId,
      enterpriseMembers
    });
  }
};

const CreateTeam: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { activeEnterprise } = useEnterprises();

  // There will always be a user with a token.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const token = user!.token;
  // TODO: When multi-enterprise is supported, we should fetch the appropriate enterprise_id
  // There should always be an active enterprise because of <Protected isEnterpriseAdmin>
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const enterpriseId = activeEnterprise!.id;

  const { pendingOrAcceptedMembers } = useV2EnterpriseMembers({
    token,
    enterpriseId
  });

  const initialTeam: Team = {
    name: '',
    user_ids: [],
    user_ids_to_add: [],
    user_ids_to_remove: []
  };

  const submit = async (team: Team) => {
    const created_team = await billingClient.teams.create({
      token,
      enterpriseId,
      name: team.name
    });

    await addUsersByIdOrEmail({
      token,
      enterpriseId,
      teamId: created_team.id,
      enterpriseMembers: pendingOrAcceptedMembers ?? [],
      userIdsOrEmails: team.user_ids_to_add
    });
  };

  return (
    <div className={globalStyles.wrap}>
      <PageTitle title={t('Create New Team')} />
      <TeamSettings
        variant={EditVariant.create}
        initialTeam={initialTeam}
        submit={submit}
      />
    </div>
  );
};

export default CreateTeam;
